import React, { useCallback, useEffect, useRef } from 'react';

import DeleteIcon from '../../assets/images/bin.svg';
import DownloadIcon from '../../assets/images/download.svg';
import EditIcon from '../../assets/images/edit.svg';
import ErrorIcon from '../../assets/images/Failed.svg';
import ViewIcon from '../../assets/images/showPass.svg';
import SuccessIcon from '../../assets/images/success-green.svg';
import APPCONSTANTS from '../../constants/appConstants';
import Checkbox from '../formComponent/Checkbox';
import Loader from '../loader/Loader';
import Pagination from '../Pagination';
import CustomTooltip from '../tooltip';
import styles from './CustomTable.module.scss';
import { IAnyObject, IColumns, ICustomProps, ICustomTableProps } from './types';

const CustomTable: React.FC<ICustomTableProps> = ({
  headerOrder = ['column', 'action', 'status'],
  dataOrder,
  columnsDef,
  rowData = [],
  isEdit = false,
  isDelete = false,
  isView = false,
  isDownload = false,
  isStatus = false,
  selectAllText,
  viewToolTip,
  page = 1,
  count = 0,
  isBordered = false,
  isRowEdit = false,
  rowDisableKey,
  checkboxEnableKey,
  loading = false,
  handlePageChange,
  onRowEdit,
  onRowView,
  onRowDownload,
  onDeleteClick,
  handleRowClick,
  checkboxState,
  selectAllState,
  isCustom = false,
  customProps = [],
  customStatusProps = [],
  rowsPerPage = 10,
  disableCheckboxWithRow = false
}) => {
  const tableRef = useRef<HTMLDivElement>(null);

  // Helper functions
  const isRowDisabled = useCallback(
    (row: IAnyObject) => {
      return rowDisableKey ? Boolean(row[rowDisableKey]) : false;
    },
    [rowDisableKey]
  );

  const isCheckboxEnabled = useCallback(
    (row: IAnyObject) => {
      // If disableCheckboxWithRow is true and row is disabled, checkbox should be disabled
      if (disableCheckboxWithRow && isRowDisabled(row)) {
        return false;
      }

      // Otherwise, check the checkbox's own enable condition
      return checkboxEnableKey ? Boolean(row[checkboxEnableKey]) : true;
    },
    [checkboxEnableKey, disableCheckboxWithRow, isRowDisabled]
  );

  // Update selected users when selectAllUser changes
  useEffect(() => {
    // Skip the effect if we don't have the required state
    if (!selectAllState || !checkboxState) {
      return;
    }

    // When selectAll is checked, select all valid users
    if (selectAllState.selectedAllUser) {
      const currentUser = { ...checkboxState.selectedUser };
      let needsUpdate = false;

      rowData.forEach((user: IAnyObject) => {
        if (!isRowDisabled(user) && isCheckboxEnabled(user)) {
          // Only update if the user isn't already selected
          if (!currentUser[user.id]?.selected) {
            currentUser[user.id] = {
              id: user.id,
              dhisId: user.userUnitId,
              firstName: user.firstName,
              lastName: user.lastName,
              selected: true
            };
            needsUpdate = true;
          }
        }
      });

      // Only update state if something changed
      if (needsUpdate) {
        checkboxState.setSelectedUser(currentUser);
      }
    }
  }, [rowData, checkboxState, isRowDisabled, isCheckboxEnabled, selectAllState]);

  // Separate effect to check if all users are selected
  useEffect(() => {
    // Skip the effect if we don't have the required state
    if (!selectAllState || !checkboxState) {
      return;
    }

    const selectedUserCount = Object.values(checkboxState.selectedUser).filter((user) => user.selected);
    const selectableUsers = rowData.filter((u: any) => !isRowDisabled(u) && isCheckboxEnabled(u));

    // Only set selectAll to true if all selectable users are selected AND there are selectable users
    if (
      selectedUserCount.length > 0 &&
      selectableUsers.length > 0 &&
      selectedUserCount.length === selectableUsers.length
    ) {
      if (!selectAllState.selectedAllUser) {
        selectAllState.setSelectedAllUser(true);
      }
    } else if (selectAllState.selectedAllUser && selectedUserCount.length !== selectableUsers.length) {
      // If selectAll is true but not all users are selected, set it to false
      selectAllState.setSelectedAllUser(false);
    }
  }, [rowData, checkboxState, selectAllState, isRowDisabled, isCheckboxEnabled]);

  // Handle page change
  const handlePageChangeInternal = (pageNo: number, rowsPerPageValue: number) => {
    if (tableRef.current) {
      tableRef.current.scrollTo(0, 0);
    }
    handlePageChange?.(pageNo, rowsPerPageValue);
  };

  // Handle row navigation
  const navigateToDetail = (data: IAnyObject) => {
    if (handleRowClick && !isRowDisabled(data)) {
      handleRowClick(data);
    }
  };

  // Handle cursor style
  const handleCursorPointerStyle = (data: IAnyObject) => {
    return handleRowClick && !isRowDisabled(data) ? styles.pointer + ' ' : '';
  };

  // Render table header
  const renderTableHeader = () => {
    // Define all possible header elements
    const headerElements = [
      {
        name: 'select',
        render: () => (
          <th className={styles.selectAll}>
            <div className='d-flex align-items-center'>
              <Checkbox
                checked={selectAllState?.selectedAllUser}
                onChange={(e) => {
                  selectAllState?.setSelectedAllUser(e.target.checked);
                  if (!e.target.checked) {
                    checkboxState?.setSelectedUser({});
                  }
                }}
              />
              <span>{selectAllText}</span>
            </div>
          </th>
        )
      },
      {
        name: 'column',
        render: () =>
          columnsDef.map((column) => (
            <th key={column.id} style={{ width: column.width, fontSize: column.fontSize }}>
              {column.label}
            </th>
          ))
      },
      {
        name: 'action',
        render: () => {
          const actions = isEdit || isDelete || isView || isDownload || isCustom;
          return actions ? <th className='text-center'>ACTIONS</th> : null;
        }
      },
      {
        name: 'status',
        render: () => (isStatus ? <th className='text-center'>STATUS</th> : null)
      }
    ];

    return (
      <thead>
        <tr>
          {
            //  Sort and filter header elements based on headerOrder
            headerOrder.map((header, index) => {
              const element = headerElements.find((config) => config.name === header);
              return element ? (
                <React.Fragment key={`header-${header}-${index}`}>{element.render()}</React.Fragment>
              ) : null;
            })
          }
        </tr>
      </thead>
    );
  };

  // Render action icons
  const renderActionIcons = (data: IAnyObject, idx: number) => {
    return (
      <div className='d-inline-flex'>
        {isEdit && (
          <div className={styles.editIcon} onClick={() => onRowEdit?.(data)}>
            <CustomTooltip title='Edit'>
              <img src={EditIcon} alt='edit-icon' />
            </CustomTooltip>
          </div>
        )}

        {isView && (
          <div className={styles.viewIcon} onClick={() => onRowView?.(data)}>
            <CustomTooltip title={viewToolTip || 'View'}>
              <img src={ViewIcon} alt='view-icon' />
            </CustomTooltip>
          </div>
        )}

        {isDownload && (
          <div className={styles.downloadIcon} onClick={() => onRowDownload?.(data)}>
            <CustomTooltip title='Download'>
              <img src={DownloadIcon} alt='download-icon' />
            </CustomTooltip>
          </div>
        )}

        {isDelete && (
          <div className={styles.deleteIcon} onClick={() => onDeleteClick?.({ data, index: idx, pageNo: page })}>
            <CustomTooltip title='Delete'>
              <img src={DeleteIcon} alt='delete-icon' />
            </CustomTooltip>
          </div>
        )}

        {renderCustomProps(customProps, data)}
      </div>
    );
  };

  // Render custom props
  const renderCustomProps = (props: ICustomProps[], data: IAnyObject) => {
    return props.map((customProp, idx) => {
      const isMOHReport = customProp.isMOHReport;
      const isSendButtonEnabled = isMOHReport && customProp.isSendButtonEnabled(data);
      const hasCustomIcon = isMOHReport ? isSendButtonEnabled : customProp.onClick !== undefined;
      const isCustomSuccessIcon = customProp?.type && customProp?.type(data) === 'success';

      return (
        <div
          key={idx}
          className={`${hasCustomIcon ? styles.customIcon : styles.customNoHoverIcon} ${
            isCustomSuccessIcon && styles.customSuccessIcon
          }`}
          onClick={(e) => customProp?.onClick && customProp?.onClick(e, data, idx)}
        >
          <CustomTooltip
            title={
              (customProp?.tooltipFormatter && customProp?.tooltipFormatter(data)) ||
              (typeof customProp?.title === 'string' ? customProp?.title : customProp?.title(data))
            }
          >
            <img
              src={
                customProp?.iconFormatter
                  ? customProp?.iconFormatter(data)
                  : typeof customProp?.icon === 'string'
                  ? customProp?.icon
                  : customProp?.icon(data)
              }
              style={typeof customProp?.style === 'object' ? customProp?.style : customProp?.style(data)}
              alt='custom-icon'
            />
          </CustomTooltip>
        </div>
      );
    });
  };

  // Render status
  const renderStatus = (data: IAnyObject) => {
    if (!isStatus) {
      return null;
    }

    if (customStatusProps && customStatusProps.length > 0) {
      return (
        <td className='text-center'>
          <div className='d-inline-flex'>{renderCustomProps(customStatusProps, data)}</div>
        </td>
      );
    }

    return (
      <td className='text-center'>
        <div className='d-inline-flex'>
          <img src={data.status ? SuccessIcon : ErrorIcon} alt='status-icon' />
        </div>
      </td>
    );
  };

  // Render checkbox
  const renderCheckbox = (data: IAnyObject) => {
    const isDisabled = !isCheckboxEnabled(data);

    return (
      headerOrder.includes('select') && (
        <td>
          <Checkbox
            disabled={isDisabled}
            checked={
              (checkboxState?.selectedUser[data?.id]?.selected || selectAllState?.selectedAllUser) && !isDisabled
            }
            name={data?.id}
            onChange={(e) => {
              if (!e.target.checked && selectAllState?.selectedAllUser) {
                selectAllState?.setSelectedAllUser(false);
              }
              const currentUser = { ...checkboxState?.selectedUser };
              currentUser[data?.id] = {
                id: data?.id,
                ...data,
                selected: e.target.checked
              };
              checkboxState?.setSelectedUser(currentUser);
            }}
          />
        </td>
      )
    );
  };

  // Render columns
  const renderColumns = (data: IAnyObject) => {
    return (
      columnsDef &&
      columnsDef.map((column: IColumns) => (
        <td
          key={column?.id}
          className={(column.class ? 'numeric' : '') + (column.cellStyleFormatter?.(data, isBordered) || '')}
        >
          <span className={column.cellStyleFormatter?.(data) || ''}>
            {column.cellFormatter ? column.cellFormatter(data, column) : data?.[column.name]}
          </span>
        </td>
      ))
    );
  };

  // Render actions
  const renderActions = (data: IAnyObject, idx: number) => {
    const actions = isEdit || isDelete || isView || isDownload || isCustom;

    return actions && <td className='text-center'>{renderActionIcons(data, idx)}</td>;
  };

  // Render pagination
  const renderPagination = () => {
    if (!count || count <= APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE) {
      return null;
    }

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
      // Scroll to top when changing rows per page
      if (tableRef && tableRef.current) {
        tableRef.current.scrollTo(0, 0);
      }

      // If the parent component provided a handlePageChange handler, call it
      if (handlePageChange) {
        handlePageChange(1, Number(newRowsPerPage));
      }
    };

    return (
      <div className={styles.paginationWrapper}>
        <Pagination
          total={count}
          currentPage={page}
          length={rowsPerPage}
          onChangePage={handlePageChangeInternal}
          initialPage={1}
          onChangeRowsPerPage={handleRowsPerPageChange}
        />
      </div>
    );
  };

  // Add this function to sort the data elements based on the headerOrder
  const getSortedDataElements = () => {
    // Define all possible data elements
    const allDataElements = [
      { name: 'select', render: renderCheckbox },
      { name: 'column', render: renderColumns },
      { name: 'action', render: renderActions },
      { name: 'status', render: renderStatus }
    ];

    // Sort the data elements based on the headerOrder
    return headerOrder.map((header) => allDataElements.find((element) => element.name === header)).filter(Boolean);
  };

  return (
    <div className={styles.customTable} ref={tableRef}>
      <table>
        {renderTableHeader()}
        <tbody>
          {rowData.length > 0 && !loading ? (
            <>
              {rowData.map((data: IAnyObject, idx: number) => (
                <tr
                  key={data.id || idx}
                  onClick={() => navigateToDetail(data)}
                  className={handleCursorPointerStyle(data) + (isRowDisabled(data) ? styles.disabled : '')}
                >
                  {getSortedDataElements().map((element, cellIndex) => (
                    <React.Fragment key={`cell-${data.id || idx}-${element?.name}-${cellIndex}`}>
                      {element?.render(data, idx)}
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </>
          ) : (
            <tr className='cursor-default'>
              <td
                colSpan={
                  isEdit || isDelete || isView || isDownload || isCustom ? columnsDef.length + 1 : columnsDef.length
                }
              >
                {loading && <Loader isFullScreen={false} />}
                {!loading && <div className='text-center'>{APPCONSTANTS.NO_RECORDS_FOUND}</div>}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {renderPagination()}
    </div>
  );
};

export default React.memo(CustomTable);
